import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { collection, getDocs, query, orderBy, onSnapshot } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { authService } from '../../services/authService';
import { buscarProductosPorTexto } from '../../utils/buscarProductos';

// ── Utilidades ────────────────────────────────────────────────────────────────

function Spinner({ sm }) {
  return <div className={`${sm ? 'w-4 h-4 border-2' : 'w-8 h-8 border-2'} border-gray-200 border-t-red-500 rounded-full animate-spin`} />;
}

const fmt = (n) => n != null
  ? Number(n).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €'
  : '—';

const fmtDate = (d) => {
  if (!d) return '—';
  const [y, m, day] = String(d).split('-');
  return `${day}/${m}/${y}`;
};

const inputCls = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-red-400 bg-white';
const labelCls = 'block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1';

function Field({ label, children }) {
  return (
    <div>
      <label className={labelCls}>{label}</label>
      {children}
    </div>
  );
}

const ESTADOS = [
  { id: 'borrador',  label: 'Borrador',   color: 'bg-gray-100 text-gray-600' },
  { id: 'enviado',   label: 'Enviado',    color: 'bg-blue-100 text-blue-700' },
  { id: 'aceptado',  label: 'Aceptado',   color: 'bg-green-100 text-green-700' },
  { id: 'rechazado', label: 'Rechazado',  color: 'bg-red-100 text-red-700' },
  { id: 'expirado',  label: 'Expirado',   color: 'bg-yellow-100 text-yellow-700' },
  { id: 'convertido',label: 'Convertido → Orden', color: 'bg-purple-100 text-purple-700' },
];

function estadoInfo(estado) {
  return ESTADOS.find(e => e.id === estado) || { label: estado, color: 'bg-gray-100 text-gray-500' };
}

const IVA = 0.21;

function calcTotales(lineas = []) {
  const base = lineas.reduce((s, l) => s + (Number(l.precio || 0) * Number(l.cantidad || 1)), 0);
  const iva = base * IVA;
  return { base, iva, total: base + iva };
}

// ── Selector de productos con stock visible ───────────────────────────────────
// NOTA: El stock se muestra para información, pero el presupuesto NO lo decrementa.
// Solo una orden cerrada descuenta stock.

function ProductoSelector({ onAdd }) {
  const [productos, setProductos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [qty, setQty] = useState(1);
  const [selected, setSelected] = useState(null);

  // Búsqueda bajo demanda contra Firestore — nada de precargar el catálogo
  // completo (~22.700 productos reales de StelOrder), ver buscarProductos.js.
  useEffect(() => {
    if (search.trim().length < 2) { setProductos([]); setLoading(false); return; }
    let vivo = true;
    setLoading(true);
    const t = setTimeout(() => {
      buscarProductosPorTexto(search, { max: 50 }).then(rows => {
        if (!vivo) return;
        setProductos(rows.map(p => ({
          ...p,
          nombre: p.name || '', referencia: p.reference || p['full-reference'] || '',
          precioVenta: p['sales-price'] ?? 0, stock: p['real-stock'] ?? 0, stockMinimo: p['minimum-stock'] ?? 0,
        })));
      }).catch(() => { if (vivo) setProductos([]); }).finally(() => { if (vivo) setLoading(false); });
    }, 300);
    return () => { vivo = false; clearTimeout(t); };
  }, [search]);

  const filtered = productos; // ya viene filtrado por buscarProductosPorTexto

  const handleAdd = () => {
    if (!selected) return;
    onAdd({
      tipo: 'producto',
      productoId: selected.id,
      descripcion: selected.nombre || selected.referencia || selected.id,
      referencia: selected.referencia || '',
      precio: Number(selected.precioVenta ?? selected.precio ?? 0),
      cantidad: Number(qty),
    });
    setSelected(null);
    setSearch('');
    setQty(1);
  };

  return (
    <div className="border border-gray-200 rounded-xl p-4 bg-gray-50 space-y-3">
      <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Buscar producto del almacén</p>
      <div className="flex gap-2">
        <input
          className={inputCls}
          placeholder="Nombre o referencia…"
          value={search}
          onChange={e => { setSearch(e.target.value); setSelected(null); }}
        />
        <input
          type="number"
          min={1}
          className="w-20 border border-gray-200 rounded-lg px-2 py-2 text-sm focus:outline-none focus:border-red-400 bg-white"
          value={qty}
          onChange={e => setQty(Math.max(1, Number(e.target.value)))}
        />
        <button
          onClick={handleAdd}
          disabled={!selected}
          className="px-4 py-2 bg-red-600 text-white text-sm font-semibold rounded-lg hover:bg-red-700 disabled:opacity-40 whitespace-nowrap"
        >
          Añadir
        </button>
      </div>
      {/* Lista de productos */}
      {search.trim().length >= 2 && (
        <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg bg-white">
          {loading ? (
            <div className="flex justify-center py-4"><Spinner sm /></div>
          ) : filtered.length === 0 ? (
            <p className="text-center text-gray-400 py-4 text-sm">Sin resultados</p>
          ) : filtered.map(p => {
            const stock = Number(p.stock ?? 0);
            const minimo = Number(p.stockMinimo ?? p.stockMin ?? 0);
            const stockOk = minimo === 0 || stock > minimo;
            return (
              <button
                key={p.id}
                onClick={() => setSelected(p)}
                className={`w-full text-left px-3 py-2.5 hover:bg-red-50 border-b border-gray-50 last:border-0 flex items-center justify-between ${selected?.id === p.id ? 'bg-red-50 border-red-200' : ''}`}
              >
                <div>
                  <div className="text-sm font-semibold text-gray-800">{p.nombre || p.id}</div>
                  <div className="text-xs text-gray-400">Ref: {p.referencia || '—'} · PVP: {fmt(p.precioVenta ?? p.precio)}</div>
                </div>
                {/* Stock visible en presupuesto — solo informativo, no se descuenta */}
                <div className={`text-xs font-semibold px-2 py-0.5 rounded-full ${stockOk ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                  Stock: {stock}
                </div>
              </button>
            );
          })}
        </div>
      )}
      {/* Aviso importante */}
      <p className="text-[10px] text-gray-400 italic">
        El stock mostrado es orientativo. El presupuesto no reserva ni descuenta stock — solo lo hace una orden cerrada.
      </p>
    </div>
  );
}

// ── Editor de líneas ──────────────────────────────────────────────────────────

function LineasEditor({ lineas, onChange }) {
  const [showProducto, setShowProducto] = useState(false);

  const update = (idx, key, val) => {
    const updated = lineas.map((l, i) => i === idx ? { ...l, [key]: val } : l);
    onChange(updated);
  };
  const remove = (idx) => onChange(lineas.filter((_, i) => i !== idx));
  const addManual = () => onChange([...lineas, { tipo: 'manual', descripcion: '', precio: 0, cantidad: 1 }]);
  const addManoObra = () => onChange([...lineas, { tipo: 'mano_obra', descripcion: 'Mano de obra', precio: 65, cantidad: 1 }]);
  const addDesplazamiento = () => onChange([...lineas, { tipo: 'desplazamiento', descripcion: 'Desplazamiento', precio: 20, cantidad: 1 }]);
  const addProducto = (prod) => { onChange([...lineas, prod]); setShowProducto(false); };

  return (
    <div className="space-y-2">
      {/* Cabecera */}
      {lineas.length > 0 && (
        <div className="grid grid-cols-12 gap-2 text-[10px] font-bold text-gray-400 uppercase px-2">
          <div className="col-span-5">Descripción</div>
          <div className="col-span-2 text-right">Precio</div>
          <div className="col-span-2 text-center">Cant.</div>
          <div className="col-span-2 text-right">Total</div>
          <div />
        </div>
      )}

      {/* Líneas */}
      {lineas.map((l, idx) => {
        const total = Number(l.precio || 0) * Number(l.cantidad || 1);
        return (
          <div key={idx} className="grid grid-cols-12 gap-2 items-center bg-gray-50 rounded-lg px-2 py-1.5">
            <input
              className="col-span-5 border-0 bg-transparent text-sm focus:outline-none text-gray-800"
              value={l.descripcion}
              onChange={e => update(idx, 'descripcion', e.target.value)}
              placeholder="Descripción…"
            />
            <input
              type="number"
              step="0.01"
              min={0}
              className="col-span-2 border-0 bg-transparent text-sm text-right focus:outline-none text-gray-800"
              value={l.precio}
              onChange={e => update(idx, 'precio', Number(e.target.value))}
            />
            <input
              type="number"
              min={1}
              className="col-span-2 border-0 bg-transparent text-sm text-center focus:outline-none text-gray-800"
              value={l.cantidad}
              onChange={e => update(idx, 'cantidad', Number(e.target.value))}
            />
            <div className="col-span-2 text-right text-sm font-semibold text-gray-700">{fmt(total)}</div>
            <button onClick={() => remove(idx)} className="text-gray-300 hover:text-red-500 text-lg leading-none">×</button>
          </div>
        );
      })}

      {/* Acciones para añadir líneas */}
      <div className="flex flex-wrap gap-2 pt-1">
        <button onClick={addManual} className="text-xs text-gray-500 hover:text-red-600 border border-dashed border-gray-200 hover:border-red-300 px-3 py-1.5 rounded-lg">
          + Línea manual
        </button>
        <button onClick={addManoObra} className="text-xs text-gray-500 hover:text-red-600 border border-dashed border-gray-200 hover:border-red-300 px-3 py-1.5 rounded-lg">
          + Mano de obra
        </button>
        <button onClick={addDesplazamiento} className="text-xs text-gray-500 hover:text-red-600 border border-dashed border-gray-200 hover:border-red-300 px-3 py-1.5 rounded-lg">
          + Desplazamiento
        </button>
        <button
          onClick={() => setShowProducto(v => !v)}
          className="text-xs text-gray-500 hover:text-red-600 border border-dashed border-gray-200 hover:border-red-300 px-3 py-1.5 rounded-lg"
        >
          + Producto almacén
        </button>
      </div>

      {showProducto && <ProductoSelector onAdd={addProducto} />}
    </div>
  );
}

// ── Formulario de presupuesto ─────────────────────────────────────────────────

const EMPTY_PRES = {
  clienteNombre: '', clienteEmail: '', clienteTelefono: '', clienteDireccion: '',
  titulo: '', descripcion: '', validezDias: 30,
  lineas: [],
  notas: '', condiciones: 'Precio válido por el período indicado. Mano de obra garantizada 1 año.',
  estado: 'borrador',
};

function PresupuestoForm({ initial, onSave, onCancel, saving }) {
  const [form, setForm] = useState(initial || EMPTY_PRES);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const { base, iva, total } = calcTotales(form.lineas);

  return (
    <div className="space-y-6">
      {/* Cliente */}
      <div>
        <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Datos del cliente</h4>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Nombre del cliente *">
            <input className={inputCls} value={form.clienteNombre} onChange={e => set('clienteNombre', e.target.value)} placeholder="Nombre completo o empresa" />
          </Field>
          <Field label="Teléfono">
            <input className={inputCls} value={form.clienteTelefono} onChange={e => set('clienteTelefono', e.target.value)} placeholder="600 000 000" />
          </Field>
          <Field label="Email">
            <input className={inputCls} type="email" value={form.clienteEmail} onChange={e => set('clienteEmail', e.target.value)} placeholder="cliente@email.com" />
          </Field>
          <Field label="Dirección">
            <input className={inputCls} value={form.clienteDireccion} onChange={e => set('clienteDireccion', e.target.value)} placeholder="Calle, número, localidad" />
          </Field>
        </div>
      </div>

      {/* Presupuesto */}
      <div>
        <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Presupuesto</h4>
        <div className="grid grid-cols-3 gap-4 mb-4">
          <div className="col-span-2">
            <Field label="Título del presupuesto *">
              <input className={inputCls} value={form.titulo} onChange={e => set('titulo', e.target.value)} placeholder="Ej: Instalación aerotermia 8kW" />
            </Field>
          </div>
          <Field label="Validez (días)">
            <input type="number" min={1} max={365} className={inputCls} value={form.validezDias} onChange={e => set('validezDias', Number(e.target.value))} />
          </Field>
        </div>
        <Field label="Descripción / alcance del trabajo">
          <textarea className={inputCls} rows={2} value={form.descripcion} onChange={e => set('descripcion', e.target.value)} placeholder="Descripción general del trabajo a realizar…" />
        </Field>
      </div>

      {/* Líneas */}
      <div>
        <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Partidas</h4>
        <LineasEditor lineas={form.lineas} onChange={ls => set('lineas', ls)} />
        {/* Totales */}
        {form.lineas.length > 0 && (
          <div className="mt-4 bg-gray-50 rounded-xl p-4 flex justify-end">
            <div className="space-y-1 text-sm min-w-[200px]">
              <div className="flex justify-between text-gray-600">
                <span>Base imponible</span>
                <span className="font-semibold">{fmt(base)}</span>
              </div>
              <div className="flex justify-between text-gray-600">
                <span>IVA (21%)</span>
                <span className="font-semibold">{fmt(iva)}</span>
              </div>
              <div className="flex justify-between text-gray-900 font-bold text-base border-t border-gray-200 pt-2 mt-2">
                <span>Total</span>
                <span>{fmt(total)}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Condiciones y notas */}
      <div className="grid grid-cols-2 gap-4">
        <Field label="Condiciones">
          <textarea className={inputCls} rows={3} value={form.condiciones} onChange={e => set('condiciones', e.target.value)} />
        </Field>
        <Field label="Notas internas">
          <textarea className={inputCls} rows={3} value={form.notas} onChange={e => set('notas', e.target.value)} placeholder="Solo visibles internamente…" />
        </Field>
      </div>

      {/* Acciones */}
      <div className="flex gap-3">
        <button
          onClick={() => onSave(form)}
          disabled={saving || !form.clienteNombre.trim() || !form.titulo.trim()}
          className="px-5 py-2 bg-red-600 text-white text-sm font-semibold rounded-lg hover:bg-red-700 disabled:opacity-50"
        >
          {saving ? 'Guardando…' : 'Guardar presupuesto'}
        </button>
        <button onClick={onCancel} className="px-5 py-2 border border-gray-200 text-sm font-semibold rounded-lg hover:bg-gray-50">
          Cancelar
        </button>
      </div>
    </div>
  );
}

// ── HTML para imprimir / email ─────────────────────────────────────────────────
function getPresupuestoHtml(pres) {
  const { base, iva, total } = calcTotales(pres.lineas || []);
  const fmtH = (n) => Number(n).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
  const lineasHtml = (pres.lineas || []).map(l => `
    <tr>
      <td style="padding:6px 8px;border-bottom:1px solid #f0f0f0;">${l.descripcion || ''}</td>
      <td style="padding:6px 8px;border-bottom:1px solid #f0f0f0;text-align:right;">${fmtH(l.precio)}</td>
      <td style="padding:6px 8px;border-bottom:1px solid #f0f0f0;text-align:center;">${l.cantidad}</td>
      <td style="padding:6px 8px;border-bottom:1px solid #f0f0f0;text-align:right;font-weight:600;">${fmtH(Number(l.precio)*Number(l.cantidad))}</td>
    </tr>`).join('');
  const fechaCreacion = pres.createdAt ? pres.createdAt.split('T')[0] : new Date().toISOString().split('T')[0];
  return `<!DOCTYPE html><html lang="es"><head><meta charset="UTF-8"/>
  <style>body{font-family:Arial,sans-serif;font-size:13px;color:#222;margin:0;padding:24px;}
  .header{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:24px;border-bottom:2px solid #c0392b;padding-bottom:16px;}
  .empresa{font-size:20px;font-weight:700;color:#c0392b;}
  .numero{font-size:16px;font-weight:700;color:#444;}
  .estado{display:inline-block;padding:2px 10px;border-radius:20px;font-size:11px;font-weight:700;background:#e8f5e9;color:#2e7d32;margin-top:6px;}
  table{width:100%;border-collapse:collapse;margin-top:16px;}
  th{padding:8px;background:#f8f8f8;text-align:left;font-size:11px;text-transform:uppercase;color:#888;border-bottom:2px solid #eee;}
  .totales{text-align:right;margin-top:16px;padding-top:12px;border-top:2px solid #eee;}
  .total-row{font-size:16px;font-weight:700;color:#c0392b;}
  .condiciones{margin-top:24px;padding:12px;background:#f9f9f9;border-radius:6px;font-size:11px;color:#666;}
  @media print{@page{margin:20mm;}}</style></head><body>
  <div class="header">
    <div><div class="empresa">Climatización Zapata</div><div style="color:#888;font-size:12px;margin-top:2px;">climatizacionzapata.es · info@climatizacionzapata.es · 966 766 306</div></div>
    <div style="text-align:right;"><div class="numero">${pres.numero || 'PRESUPUESTO'}</div><div class="estado">Presupuesto</div><div style="color:#888;font-size:11px;margin-top:4px;">Fecha: ${fechaCreacion} · Validez: ${pres.validezDias || 30} días</div></div>
  </div>
  <div style="margin-bottom:16px;"><strong>Cliente:</strong> ${pres.clienteNombre || '—'}<br/>
  ${pres.clienteDireccion ? `<span style="color:#666;">${pres.clienteDireccion}</span><br/>` : ''}
  ${pres.clienteEmail ? `<span style="color:#666;">${pres.clienteEmail}</span>` : ''}</div>
  <div style="font-size:16px;font-weight:700;margin-bottom:4px;">${pres.titulo || ''}</div>
  ${pres.descripcion ? `<div style="color:#666;margin-bottom:12px;font-size:12px;">${pres.descripcion.replace(/\n/g,'<br/>')}</div>` : ''}
  <table><thead><tr><th>Descripción</th><th style="text-align:right;">P.Unit.</th><th style="text-align:center;">Ud.</th><th style="text-align:right;">Total</th></tr></thead>
  <tbody>${lineasHtml}</tbody></table>
  <div class="totales">
    <div>Base imponible: <strong>${fmtH(base)}</strong></div>
    <div style="margin-top:4px;">IVA (21%): <strong>${fmtH(iva)}</strong></div>
    <div class="total-row" style="margin-top:8px;">TOTAL: ${fmtH(total)}</div>
  </div>
  ${pres.condiciones ? `<div class="condiciones"><strong>Condiciones:</strong><br/>${pres.condiciones.replace(/\n/g,'<br/>')}</div>` : ''}
  </body></html>`;
}

// ── Detalle de presupuesto (vista de lectura con acciones) ────────────────────

function PresupuestoDetalle({ pres, onClose, onUpdate }) {
  const [saving, setSaving] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailTo, setEmailTo] = useState(pres.clienteEmail || '');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailResult, setEmailResult] = useState('');
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [showFirmaModal, setShowFirmaModal] = useState(false);
  const [firmaEmail, setFirmaEmail] = useState(pres.clienteEmail || '');
  const [sendingFirma, setSendingFirma] = useState(false);
  const [firmaResult, setFirmaResult] = useState('');
  const [firmaUrl, setFirmaUrl] = useState('');
  const { base, iva, total } = calcTotales(pres.lineas || []);

  const cambiarEstado = async (estado) => {
    setSaving(true);
    try {
      const resp = await fetch('/.netlify/functions/savePresupuesto', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify({ id: pres.id, estado }),
      });
      const d = await resp.json();
      if (d.success) onUpdate({ ...pres, estado });
    } finally {
      setSaving(false);
    }
  };

  const handleEnviarEmail = async () => {
    if (!emailTo.trim()) return;
    setSendingEmail(true);
    setEmailResult('');
    try {
      const html = getPresupuestoHtml(pres);
      const resp = await fetch('/.netlify/functions/sendEmail', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify({
          to: emailTo.trim(),
          subject: `Presupuesto ${pres.numero || ''} — ${pres.titulo}`,
          body: html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 200) + '...',
          attachments: [{
            filename: `presupuesto-${pres.numero || 'ZAP'}.html`,
            content: Buffer.from ? Buffer.from(html).toString('base64') : btoa(unescape(encodeURIComponent(html))),
            contentType: 'text/html',
          }],
        }),
      });
      const d = await resp.json();
      if (d.success || d.messageId) {
        setEmailResult('ok');
        if (pres.estado === 'borrador') await cambiarEstado('enviado');
      } else {
        setEmailResult(d.error || 'Error al enviar');
      }
    } catch (e) {
      setEmailResult(e.message);
    } finally {
      setSendingEmail(false);
    }
  };

  const handleEnviarParaFirma = async () => {
    if (!firmaEmail.trim()) return;
    setSendingFirma(true);
    setFirmaResult('');
    try {
      const resp = await fetch('/.netlify/functions/crearTokenFirmaPresupuesto', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify({ presupuestoId: pres.id, emailDestinatario: firmaEmail.trim() }),
      });
      const data = await resp.json();
      if (!data.success) throw new Error(data.error || 'No se pudo crear el enlace de firma');
      setFirmaUrl(data.url);
      setFirmaResult('ok');
      if (pres.estado === 'borrador') await cambiarEstado('enviado');
    } catch (e) {
      setFirmaResult(e.message);
    } finally {
      setSendingFirma(false);
    }
  };

  const handleImprimir = () => {
    const win = window.open('', '_blank', 'width=800,height=600');
    if (!win) return;
    win.document.write(getPresupuestoHtml(pres));
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 400);
  };

  const handleConvertirOrden = async () => {
    setShowConvertModal(false);
    setSaving(true);
    try {
      const resp = await fetch('/.netlify/functions/saveOrden', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify({
          clienteNombre:     pres.clienteNombre,
          descripcion:       `${pres.titulo}${pres.descripcion ? '\n' + pres.descripcion : ''}`,
          estado:            'pendiente',
          tipo:              'instalacion',
          lineas:            pres.lineas || [],
          origen:            'presupuesto',
          presupuestoId:     pres.id,
          presupuestoNumero: pres.numero,
        }),
      });
      const d = await resp.json();
      if (d.success) {
        const updated = { ...pres, estado: 'convertido', ordenId: d.id, ordenReferencia: d.referencia };
        await fetch('/.netlify/functions/savePresupuesto', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
          body: JSON.stringify({ id: pres.id, estado: 'convertido', ordenId: d.id, ordenReferencia: d.referencia }),
        });
        onUpdate(updated);
        alert(`Orden de trabajo creada: ${d.referencia}`);
      } else {
        alert('Error: ' + (d.error || 'No se pudo crear la orden'));
      }
    } catch (e) {
      alert('Error: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleConvertirInstalacion = async () => {
    setShowConvertModal(false);
    setSaving(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      const resp = await fetch('/.netlify/functions/saveInstalacion', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify({
          clienteNombre:    pres.clienteNombre,
          clienteEmail:     pres.clienteEmail     || '',
          clienteTelefono:  pres.clienteTelefono  || '',
          clienteDireccion: pres.clienteDireccion || '',
          descripcion:      [pres.titulo, pres.descripcion].filter(Boolean).join('\n'),
          lineas:           pres.lineas || [],
          importeTotal:     total,
          presupuestoRef:   pres.numero,
          presupuestoId:    pres.id,
          estado:           'en_curso',
          fases: {
            presupuesto:  { completada: true,  fecha: today, notas: `Importado de ${pres.numero}` },
            material:     { completada: false, fecha: null, notas: '' },
            instalacion:  { completada: false, fecha: null, notas: '' },
            pruebas:      { completada: false, fecha: null, notas: '' },
            finalizacion: { completada: false, fecha: null, notas: '' },
            entrega:      { completada: false, fecha: null, notas: '' },
          },
        }),
      });
      const d = await resp.json();
      if (d.success) {
        await fetch('/.netlify/functions/savePresupuesto', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
          body: JSON.stringify({ id: pres.id, estado: 'convertido', instalacionId: d.id, instalacionRef: d.numero }),
        });
        onUpdate({ ...pres, estado: 'convertido', instalacionId: d.id, instalacionRef: d.numero });
        alert(`Instalación creada: ${d.numero}`);
      } else {
        alert('Error: ' + (d.error || 'No se pudo crear la instalación'));
      }
    } catch (e) {
      alert('Error: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  const ei = estadoInfo(pres.estado);

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="p-6 border-b border-gray-100 flex items-center justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3">
              <h3 className="text-xl font-bold text-gray-800">{pres.numero}</h3>
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${ei.color}`}>{ei.label}</span>
            </div>
            <p className="text-gray-500 text-sm mt-0.5 truncate">{pres.titulo}</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={handleImprimir}
              title="Imprimir / PDF"
              className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 font-medium"
            >
              🖨️ Imprimir
            </button>
            <button
              onClick={() => { setShowEmailModal(true); setEmailTo(pres.clienteEmail || ''); setEmailResult(''); }}
              title="Enviar por email"
              className="px-3 py-1.5 border border-blue-200 bg-blue-50 rounded-lg text-sm text-blue-700 hover:bg-blue-100 font-medium"
            >
              ✉️ Email
            </button>
            {(pres.estado === 'borrador' || pres.estado === 'enviado') && (
              <button
                onClick={() => { setShowFirmaModal(true); setFirmaEmail(pres.clienteEmail || ''); setFirmaResult(''); setFirmaUrl(''); }}
                title="Enviar enlace de firma digital"
                className="px-3 py-1.5 border border-indigo-200 bg-indigo-50 rounded-lg text-sm text-indigo-700 hover:bg-indigo-100 font-medium"
              >
                ✍️ Firma
              </button>
            )}
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none ml-1">×</button>
          </div>
        </div>

        {/* Modal firma digital */}
        {showFirmaModal && (
          <div className="mx-6 my-3 bg-indigo-50 border border-indigo-200 rounded-xl p-4 space-y-3">
            <p className="text-sm font-bold text-indigo-800">✍️ Enviar enlace de firma digital</p>
            {firmaResult !== 'ok' ? (
              <>
                <p className="text-xs text-indigo-600">El cliente recibirá un enlace para firmar el presupuesto desde su móvil u ordenador. Válido 7 días.</p>
                <input
                  type="email"
                  className="w-full border border-indigo-200 bg-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-indigo-400"
                  placeholder="Email del cliente"
                  value={firmaEmail}
                  onChange={e => setFirmaEmail(e.target.value)}
                />
                {firmaResult && <p className="text-xs text-red-600">{firmaResult}</p>}
                <div className="flex gap-2">
                  <button
                    onClick={handleEnviarParaFirma}
                    disabled={sendingFirma || !firmaEmail.trim()}
                    className="px-4 py-2 bg-indigo-600 text-white text-sm font-semibold rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                  >
                    {sendingFirma ? 'Enviando…' : 'Enviar enlace de firma'}
                  </button>
                  <button onClick={() => setShowFirmaModal(false)} className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">
                    Cancelar
                  </button>
                </div>
              </>
            ) : (
              <div className="space-y-2">
                <p className="text-xs text-green-700 font-semibold">✓ Enlace enviado a {firmaEmail}</p>
                <div className="flex gap-2 items-center bg-white border border-indigo-200 rounded-lg px-3 py-2">
                  <p className="text-xs text-gray-500 truncate flex-1 font-mono">{firmaUrl}</p>
                  <button
                    onClick={() => navigator.clipboard?.writeText(firmaUrl)}
                    className="text-xs text-indigo-600 hover:text-indigo-800 font-semibold whitespace-nowrap"
                  >
                    Copiar
                  </button>
                </div>
                <button onClick={() => setShowFirmaModal(false)} className="text-xs text-gray-500 hover:text-gray-700 underline">Cerrar</button>
              </div>
            )}
          </div>
        )}

        {/* Modal envío email */}
        {showEmailModal && (
          <div className="mx-6 my-3 bg-blue-50 border border-blue-200 rounded-xl p-4 space-y-3">
            <p className="text-sm font-bold text-blue-800">Enviar presupuesto por email</p>
            <input
              type="email"
              className="w-full border border-blue-200 bg-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-400"
              placeholder="Dirección de email del cliente"
              value={emailTo}
              onChange={e => setEmailTo(e.target.value)}
            />
            {emailResult === 'ok' && <p className="text-xs text-green-700 font-semibold">✓ Email enviado correctamente</p>}
            {emailResult && emailResult !== 'ok' && <p className="text-xs text-red-600">{emailResult}</p>}
            <div className="flex gap-2">
              <button
                onClick={handleEnviarEmail}
                disabled={sendingEmail || !emailTo.trim()}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {sendingEmail ? 'Enviando…' : 'Enviar'}
              </button>
              <button onClick={() => setShowEmailModal(false)} className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">
                Cancelar
              </button>
            </div>
          </div>
        )}

        <div className="p-6 space-y-6">
          {/* Datos cliente */}
          <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm">
            <div><span className="text-gray-400">Cliente: </span><span className="font-semibold">{pres.clienteNombre}</span></div>
            <div><span className="text-gray-400">Teléfono: </span><span className="font-semibold">{pres.clienteTelefono || '—'}</span></div>
            <div><span className="text-gray-400">Email: </span><span className="font-semibold">{pres.clienteEmail || '—'}</span></div>
            <div><span className="text-gray-400">Fecha: </span><span className="font-semibold">{fmtDate(pres.createdAt?.split('T')[0])}</span></div>
            <div><span className="text-gray-400">Validez: </span><span className="font-semibold">{pres.validezDias || 30} días</span></div>
            <div><span className="text-gray-400">Dirección: </span><span className="font-semibold">{pres.clienteDireccion || '—'}</span></div>
          </div>

          {/* Descripción */}
          {pres.descripcion && (
            <div>
              <p className={labelCls}>Descripción</p>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{pres.descripcion}</p>
            </div>
          )}

          {/* Líneas */}
          <div>
            <p className={labelCls}>Partidas</p>
            <table className="w-full text-sm mt-1">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left py-1 text-xs text-gray-400 font-bold">Descripción</th>
                  <th className="text-right py-1 text-xs text-gray-400 font-bold">Precio</th>
                  <th className="text-center py-1 text-xs text-gray-400 font-bold">Ud.</th>
                  <th className="text-right py-1 text-xs text-gray-400 font-bold">Total</th>
                </tr>
              </thead>
              <tbody>
                {(pres.lineas || []).map((l, i) => (
                  <tr key={i} className="border-b border-gray-50">
                    <td className="py-1.5 text-gray-700">{l.descripcion}</td>
                    <td className="py-1.5 text-right text-gray-600">{fmt(l.precio)}</td>
                    <td className="py-1.5 text-center text-gray-600">{l.cantidad}</td>
                    <td className="py-1.5 text-right font-semibold text-gray-800">{fmt(Number(l.precio) * Number(l.cantidad))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="mt-3 text-right space-y-1 text-sm">
              <div className="text-gray-500">Base: <span className="font-semibold text-gray-800">{fmt(base)}</span></div>
              <div className="text-gray-500">IVA 21%: <span className="font-semibold text-gray-800">{fmt(iva)}</span></div>
              <div className="text-base font-bold text-gray-900">Total: {fmt(total)}</div>
            </div>
          </div>

          {/* Condiciones */}
          {pres.condiciones && (
            <div>
              <p className={labelCls}>Condiciones</p>
              <p className="text-xs text-gray-500 whitespace-pre-wrap">{pres.condiciones}</p>
            </div>
          )}

          {/* Acciones de estado */}
          <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-100">
            {pres.estado === 'borrador' && (
              <button
                onClick={() => cambiarEstado('enviado')}
                disabled={saving}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                Marcar como enviado
              </button>
            )}
            {(pres.estado === 'enviado' || pres.estado === 'borrador') && (
              <>
                <button
                  onClick={() => cambiarEstado('aceptado')}
                  disabled={saving}
                  className="px-4 py-2 bg-green-600 text-white text-sm font-semibold rounded-lg hover:bg-green-700 disabled:opacity-50"
                >
                  Marcar como aceptado
                </button>
                <button
                  onClick={() => cambiarEstado('rechazado')}
                  disabled={saving}
                  className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-semibold rounded-lg hover:bg-gray-200 disabled:opacity-50"
                >
                  Rechazado
                </button>
              </>
            )}
            {pres.estado === 'aceptado' && (
              <button
                onClick={() => setShowConvertModal(true)}
                disabled={saving}
                className="px-4 py-2 bg-purple-600 text-white text-sm font-semibold rounded-lg hover:bg-purple-700 disabled:opacity-50"
              >
                {saving ? 'Creando…' : 'Convertir presupuesto'}
              </button>
            )}
            {pres.estado === 'convertido' && (pres.ordenReferencia || pres.instalacionRef) && (
              <div className="flex items-center gap-2 bg-purple-50 rounded-lg px-3 py-2">
                <span className="text-xs text-purple-700 font-semibold">
                  {pres.ordenReferencia ? `Orden: ${pres.ordenReferencia}` : `Instalación: ${pres.instalacionRef}`}
                </span>
              </div>
            )}

            {/* Modal de conversión */}
            {showConvertModal && (
              <div className="fixed inset-0 bg-black/50 z-[70] flex items-center justify-center p-4" onClick={() => setShowConvertModal(false)}>
                <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm" onClick={e => e.stopPropagation()}>
                  <div className="p-5 border-b border-gray-100">
                    <h4 className="font-bold text-gray-800">¿Cómo convertir este presupuesto?</h4>
                    <p className="text-xs text-gray-500 mt-1">{pres.clienteNombre} · {pres.numero}</p>
                  </div>
                  <div className="p-5 space-y-3">
                    <button
                      onClick={handleConvertirOrden}
                      className="w-full flex items-center gap-3 p-4 rounded-xl border-2 border-gray-200 hover:border-blue-400 hover:bg-blue-50 transition-colors text-left"
                    >
                      <span className="text-2xl">🔧</span>
                      <div>
                        <div className="font-bold text-gray-800 text-sm">Orden de trabajo SAT</div>
                        <div className="text-xs text-gray-500">Reparación o servicio técnico puntual</div>
                      </div>
                    </button>
                    <button
                      onClick={handleConvertirInstalacion}
                      className="w-full flex items-center gap-3 p-4 rounded-xl border-2 border-gray-200 hover:border-purple-400 hover:bg-purple-50 transition-colors text-left"
                    >
                      <span className="text-2xl">🏗️</span>
                      <div>
                        <div className="font-bold text-gray-800 text-sm">Proyecto de instalación</div>
                        <div className="text-xs text-gray-500">Instalación nueva con seguimiento por fases</div>
                      </div>
                    </button>
                    <button
                      onClick={() => setShowConvertModal(false)}
                      className="w-full py-2 text-sm text-gray-500 hover:text-gray-700"
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Vista principal ───────────────────────────────────────────────────────────

export default function PresupuestosView() {
  const [presupuestos, setPresupuestos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detalle, setDetalle] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [estadoFilter, setEstadoFilter] = useState('');

  const cargar = useCallback(() => {
    setLoading(true);
    fetch('/.netlify/functions/getPresupuestos', { headers: { ...authService.authHeader() } })
      .then(r => r.json())
      .then(d => { setPresupuestos(d.success ? d.presupuestos : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  // Prefill al llegar desde una incidencia de tipo "presupuesto" en el CRM
  // (ver IncidenciasTab.js → botón "Crear presupuesto"). Mismo patrón que
  // zapata_crm_prefill (CRMEmailView.js → CRM). `parentIncidentId` viaja
  // dentro del form sin campo visible propio — savePresupuesto.js lo usa
  // para cerrar la incidencia de origen al guardar.
  const [prefillNuevo, setPrefillNuevo] = useState(null);
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem('zapata_presupuesto_prefill');
      if (raw) {
        sessionStorage.removeItem('zapata_presupuesto_prefill');
        setPrefillNuevo({ ...EMPTY_PRES, ...JSON.parse(raw) });
        setEditing(null);
        setShowForm(true);
      }
    } catch { /* prefill opcional — si falla, se abre el formulario en blanco */ }
  }, []);

  const filtered = useMemo(() => {
    let list = presupuestos;
    if (estadoFilter) list = list.filter(p => p.estado === estadoFilter);
    if (search.trim()) {
      const s = search.toLowerCase();
      list = list.filter(p =>
        (p.numero || '').toLowerCase().includes(s) ||
        (p.clienteNombre || '').toLowerCase().includes(s) ||
        (p.titulo || '').toLowerCase().includes(s)
      );
    }
    return list;
  }, [presupuestos, search, estadoFilter]);

  const handleSave = async (form) => {
    setSaving(true);
    setError('');
    try {
      const payload = editing ? { ...form, id: editing.id } : form;
      const resp = await fetch('/.netlify/functions/savePresupuesto', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify(payload),
      });
      const d = await resp.json();
      if (!d.success) throw new Error(d.error || 'Error al guardar');
      cargar();
      setShowForm(false);
      setEditing(null);
      setPrefillNuevo(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = (updated) => {
    setPresupuestos(prev => prev.map(p => p.id === updated.id ? updated : p));
    setDetalle(updated);
  };

  const totales = useMemo(() => {
    const t = { total: 0, base: 0 };
    presupuestos.filter(p => p.estado === 'aceptado').forEach(p => {
      const { base, total } = calcTotales(p.lineas || []);
      t.base += base;
      t.total += total;
    });
    return t;
  }, [presupuestos]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-2xl shadow-lg border p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Presupuestos</h2>
            <p className="text-gray-500 text-sm mt-1">Gestión de presupuestos digitales con líneas de material y mano de obra</p>
          </div>
          <button
            onClick={() => { setEditing(null); setPrefillNuevo(null); setShowForm(true); setError(''); }}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white text-sm font-semibold rounded-lg hover:bg-red-700"
          >
            <span className="text-lg leading-none">+</span> Nuevo presupuesto
          </button>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-4 gap-4 mt-4">
          {[
            { label: 'Total', val: presupuestos.length, color: 'text-gray-800' },
            { label: 'Aceptados', val: presupuestos.filter(p => p.estado === 'aceptado').length, color: 'text-green-600' },
            { label: 'Pendientes', val: presupuestos.filter(p => p.estado === 'enviado').length, color: 'text-blue-600' },
            { label: 'Importe aceptado', val: fmt(totales.total), color: 'text-red-600' },
          ].map(k => (
            <div key={k.label} className="bg-gray-50 rounded-xl p-3 text-center">
              <div className={`text-2xl font-bold ${k.color}`}>{k.val}</div>
              <div className="text-xs text-gray-500 mt-0.5">{k.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Formulario */}
      {showForm && (
        <div className="bg-white rounded-2xl shadow-lg border p-6">
          <h3 className="font-bold text-gray-800 mb-4">{editing ? `Editar ${editing.numero}` : 'Nuevo presupuesto'}</h3>
          {error && <div className="bg-red-50 text-red-700 text-sm rounded-lg px-4 py-2 mb-4">{error}</div>}
          <PresupuestoForm
            initial={editing || prefillNuevo || EMPTY_PRES}
            onSave={handleSave}
            onCancel={() => { setShowForm(false); setEditing(null); setPrefillNuevo(null); setError(''); }}
            saving={saving}
          />
        </div>
      )}

      {/* Lista */}
      <div className="bg-white rounded-2xl shadow-lg border p-6 space-y-4">
        {/* Filtros */}
        <div className="flex gap-3">
          <input
            className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-red-400"
            placeholder="Buscar por número, cliente o título…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <select
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-red-400"
            value={estadoFilter}
            onChange={e => setEstadoFilter(e.target.value)}
          >
            <option value="">Todos los estados</option>
            {ESTADOS.map(e => <option key={e.id} value={e.id}>{e.label}</option>)}
          </select>
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><Spinner /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            {presupuestos.length === 0 ? 'No hay presupuestos. Crea el primero.' : 'Sin resultados.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left py-2 px-3 text-xs font-bold text-gray-400 uppercase">Número</th>
                  <th className="text-left py-2 px-3 text-xs font-bold text-gray-400 uppercase">Cliente</th>
                  <th className="text-left py-2 px-3 text-xs font-bold text-gray-400 uppercase">Título</th>
                  <th className="text-left py-2 px-3 text-xs font-bold text-gray-400 uppercase">Fecha</th>
                  <th className="text-right py-2 px-3 text-xs font-bold text-gray-400 uppercase">Total (IVA inc.)</th>
                  <th className="text-left py-2 px-3 text-xs font-bold text-gray-400 uppercase">Estado</th>
                  <th />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filtered.map(p => {
                  const { total } = calcTotales(p.lineas || []);
                  const ei = estadoInfo(p.estado);
                  return (
                    <tr key={p.id} className="hover:bg-gray-50 cursor-pointer group" onClick={() => setDetalle(p)}>
                      <td className="py-3 px-3 font-mono text-xs font-semibold text-gray-600">{p.numero || '—'}</td>
                      <td className="py-3 px-3 font-semibold text-gray-800">{p.clienteNombre}</td>
                      <td className="py-3 px-3 text-gray-600 max-w-[200px] truncate">{p.titulo}</td>
                      <td className="py-3 px-3 text-gray-500">{fmtDate(p.createdAt?.split('T')[0])}</td>
                      <td className="py-3 px-3 text-right font-bold text-gray-800">{fmt(total)}</td>
                      <td className="py-3 px-3">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${ei.color}`}>{ei.label}</span>
                      </td>
                      <td className="py-3 px-3">
                        <button
                          onClick={e => { e.stopPropagation(); setEditing(p); setShowForm(true); setError(''); }}
                          className="text-xs text-gray-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          Editar
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal detalle */}
      {detalle && (
        <PresupuestoDetalle
          pres={detalle}
          onClose={() => setDetalle(null)}
          onUpdate={handleUpdate}
        />
      )}
    </div>
  );
}
